import React, { useMemo } from 'react';
import { InputWrapper } from '../../common/input-wrapper';
import useSWR from 'swr';
import { fetcher } from '../../../services/api';
import { getDeliveryPeriod, toMoney } from '../../../utils/common.utils';
import { Link, NavLink } from 'react-router-dom';
import { IdCell } from '../../common/cards/id-cell-card.component';
import {
  CleanDeliveryOrder,
  RbacObject,
} from '../../../export-types/cleaned-types';
import { useProfile } from '../../../api/profile';
import { FormattedMessage, useIntl } from 'react-intl';
import { Rbac, useRbac } from '../../common/Rbac';
import { DeliveryOrderRequestInfoActions } from './delivery-order-request-info-actions';
import { RequestAddressEditForm } from './request-address-edit-form';
import { RequestDeliveryMethodEditForm } from './request-delivery-method-edit-form';
import { Decimal } from 'decimal.js';
import { useOrderDeliveryPaths } from '../purchase-order/useOrderPaths';
import {
  useDeliveryMethodsList,
  usePackageMethods,
} from '../../../consts/db-values';
import { DeliveryOrderRelatedParcels } from './delivery-order-related-parcels';

type DeliveryOrderDeliveryInfoProps = {
  id: string;
};
export const DeliveryOrderDeliveryInfo: React.FC<
  DeliveryOrderDeliveryInfoProps
> = (props) => {
  const [editModeAddress, setEditModeAddress] = React.useState(false);
  const [editModeDeliveryMethod, setEditModeDeliveryMethod] =
    React.useState(false);
  const { data: user } = useProfile();
  const { hasPermission } = useRbac();
  const { data: order, mutate } = useSWR<CleanDeliveryOrder>(
    `/delivery-order/${props.id}`,
    fetcher,
  );
  const intl = useIntl();
  const { countryFrom } = useOrderDeliveryPaths({ orderId: props.id });

  // Move hooks before early return to avoid conditional hook calls
  const PACKAGE_METHODS = usePackageMethods();
  const DELIVERY_METHODS_LIST = useDeliveryMethodsList();

  const totalWeight = useMemo(() => {
    if (order?.purchaseOrders) {
      const total = order.purchaseOrders?.reduce(
        (a, b) => a.plus(new Decimal(b.weight || 0)),
        new Decimal(0),
      );
      return total.toFixed(2);
    }
    return '';
  }, [order]);

  const totalVolume = useMemo(() => {
    if (order?.purchaseOrders) {
      const total = order.purchaseOrders?.reduce(
        (a, b) => a.plus(new Decimal(b.volume || 0)),
        new Decimal(0),
      );
      return total.toFixed(3);
    }

    return '';
  }, [order]);

  const methodLabels = useMemo(() => {
    if (order?.packageGroups?.length === 0) {
      const methods = order?.packageMethods;
      if (!methods) return '';

      const methodNames = methods.split(',').map((method) => method.trim());
      return (
        PACKAGE_METHODS.filter((method) => methodNames.includes(method.name))
          .map((method) => method.label)
          .join(', ') || methods
      );
    } else {
      return order?.packageGroups
        ?.flatMap((group) => group.methods)
        .map((method) => {
          const foundMethod = PACKAGE_METHODS.find(
            (pm) => pm.name === method.name,
          );
          return foundMethod ? foundMethod.label : method.name;
        })
        .join(', ');
    }
  }, [order?.packageMethods, order?.packageGroups, PACKAGE_METHODS]);

  const deliveryLabel = useMemo(() => {
    if (!order?.deliveryMethod) {
      return '';
    }
    const deliveryMethod = DELIVERY_METHODS_LIST.find(
      (item) => item.name === order.deliveryMethod?.name,
    );
    return deliveryMethod ? deliveryMethod.label : order.deliveryMethod.name;
  }, [order?.deliveryMethod, DELIVERY_METHODS_LIST]);

  if (!order || !user) {
    return null;
  }

  function handleEditAddress() {
    setEditModeAddress(true);
  }

  function handleEditDeliveryMethod() {
    setEditModeDeliveryMethod(true);
  }

  return (
    <div>
      <div className="d-flex flex-row justify-content-between">
        <div className="fs-18 fw-600 color-gray-450">
          <FormattedMessage
            defaultMessage="О заказе"
            id="orderInfo.label.aboutOrder"
            description="Информация о заказе"
          />
        </div>
        <Rbac object={RbacObject.DeliveryOrder} action={'write:address'}>
          <DeliveryOrderRequestInfoActions
            order={order}
            onEditDeliveryMethod={handleEditDeliveryMethod}
            onEditAddress={handleEditAddress}
          />
        </Rbac>
      </div>
      <table className="table table-xs table-borderless mt-3">
        <thead className="color-gray-600">
          <tr>
            <td>
              <FormattedMessage
                defaultMessage="Выкупы в доставке"
                id="deliveryInfo.label.ordersIsInDelivery"
                description="Подзаголовок на странице"
              />
            </td>
            <td className="text-end">
              <FormattedMessage
                defaultMessage="Место хранения"
                id="storage.label.storagePlace"
                description="Информация о месте хранения"
              />
            </td>
          </tr>
        </thead>
        <tbody>
          {order.purchaseOrders?.map(
            ({ internalId, type, id, storageLocation }, i) => (
              <tr key={i}>
                <td>
                  <NavLink
                    className="btn-link cursor-pointer text-decoration-underline"
                    to={`/${
                      type === 'purchase' ? 'purchases' : 'parcels'
                    }/${id}`}
                  >
                    <IdCell internalId={internalId} type={type} />
                  </NavLink>
                </td>
                <td className="text-end">{storageLocation}</td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      {hasPermission(RbacObject.DeliveryOrder, 'read') && (
        <DeliveryOrderRelatedParcels deliveryOrder={order} />
      )}
      {order.transportation?.type === 'combined' && (
        <div className={'mt-3'}>
          Транспортировка в составе сбрной
          <span>
            {' '}
            <Link to={`/transportations/${order.transportation.id}`}>
              {order.transportation.name}
            </Link>
          </span>
        </div>
      )}
      {!user?.accesses.includes('fulfillment') && (
        <InputWrapper
          theme="light"
          className="mt-3"
          title={intl.formatMessage({
            defaultMessage: 'Общая стоимость товаров в заказе',
            id: 'deliveryInfo.input-label.totalCostOfGoodsInTheOrder',
            description: 'Заголовок поля ввода',
          })}
        >
          {toMoney(
            order.moderateProductPrice!,
            order.supplier?.purchaseCurrency || 'cny',
          )}
        </InputWrapper>
      )}
      <InputWrapper
        theme="light"
        className="mt-3"
        title={intl.formatMessage({
          defaultMessage: 'Общий вес посылок',
          id: 'delivery.input-label.totalWeightOfParcels',
          description: 'Заголовок поля ввода',
        })}
      >
        <FormattedMessage
          defaultMessage="{value} кг"
          id="delivery.labelTotalWeight.kg"
          description="Единица измерения"
          values={{ value: totalWeight }}
        />
      </InputWrapper>
      <InputWrapper
        theme="light"
        className="mt-3"
        title={intl.formatMessage({
          defaultMessage: 'Общий объем посылок',
          id: 'delivery.input-label.totalVolumeOfParcels',
          description: 'Заголовок поля ввода',
        })}
      >
        <FormattedMessage
          defaultMessage="{value} м³"
          id="delivery.label.length"
          description="Единица измерения"
          values={{ value: totalVolume }}
        />
      </InputWrapper>
      <InputWrapper
        theme="light"
        className="mt-3"
        title={intl.formatMessage({
          defaultMessage: 'Упаковка',
          id: 'delivery.title-label.packaging',
          description: 'Заголовок поля ввода',
        })}
      >
        {methodLabels}
      </InputWrapper>
      {order.storageLocation && (
        <InputWrapper
          theme="light"
          className="mt-3"
          title={intl.formatMessage({
            defaultMessage: 'Место хранения',
            id: 'delivery.title-label.storageLocation',
            description: 'Заголовок поля ввода',
          })}
        >
          {order.storageLocation}
        </InputWrapper>
      )}

      {editModeDeliveryMethod ? (
        <RequestDeliveryMethodEditForm
          id={order.id}
          deliveryMethod={order.deliveryMethod?.name}
          purchaseOrders={
            order.purchaseOrders?.map((purchase) => purchase.id) ?? []
          }
          onCancel={() => {
            setEditModeDeliveryMethod(false);
          }}
          onSave={() => {
            mutate();
            setEditModeDeliveryMethod(false);
          }}
        />
      ) : (
        <InputWrapper
          theme="light"
          className="mt-3"
          title={intl.formatMessage(
            {
              defaultMessage: 'Доставка {country} — РФ',
              id: 'deliveryInfo.input-label.deliveryChinaRussia',
              description: 'Заголовок поля ввода',
            },
            { country: countryFrom },
          )}
        >
          {order.deliveryMethod ? (
            <>
              {deliveryLabel}, {getDeliveryPeriod(order.deliveryMethod)}
            </>
          ) : null}
          {order.retailOrder && <>{'Авто + Авиа, 13 — 15 дней'}</>}
        </InputWrapper>
      )}
      {editModeAddress && order.address ? (
        <RequestAddressEditForm
          supplierId={order.supplier?.id}
          deliveryMethod={order.deliveryMethod}
          defaultValues={{
            localDelivery: order.localDeliveryMethod.id,
            type: order.address?.type,
            name: order.address.name,
            city: order.address?.city,
            companyInn: order.address?.companyInn ?? undefined,
            phone: order.address.phone,
            companyName: order.address?.companyName ?? undefined,
            passport: order.address?.passport ?? undefined,
            pickupPoint: order.address?.pickupPoint ?? undefined,
            cdekCode: order.cdekOrder?.pointCode ?? undefined,
            isCdek: order.localDeliveryMethod.isCdek,
            fullAddress: order.address.fullAddress ?? undefined,
          }}
          id={order.id}
          onCancel={() => {
            setEditModeAddress(false);
          }}
          onSave={() => {
            mutate();
            setEditModeAddress(false);
          }}
        />
      ) : (
        <>
          <InputWrapper
            theme="light"
            className="mt-3"
            title={intl.formatMessage({
              defaultMessage: 'Доставка по РФ',
              id: 'deliveryInfo.input-label.shippingWithinTheRussianFederation',
              description: 'Заголовок поля ввода',
            })}
          >
            {order.localDeliveryMethod ? order.localDeliveryMethod.name : ''}
            {order.retailOrder?.pickupPoint &&
              order.retailOrder.pickupPoint.lockerContractor}
          </InputWrapper>
          <Rbac object={RbacObject.DeliveryOrder} action={'read:address'}>
            <InputWrapper
              theme="light"
              className="mt-3"
              title={intl.formatMessage({
                defaultMessage: 'Адрес доставки и получатель',
                id: 'deliveryInfo.input-label.shippingAddressAndRecipient',
                description: 'Заголовок поля ввода',
              })}
            >
              {order.address && !order.retailOrder ? (
                <>
                  <div>
                    г.{order.address.city}, {order.address.name}
                  </div>
                  <div>Адрес: {order.address.fullAddress}</div>
                  {order.address.type === 'legal' && (
                    <div>
                      {order.address.companyName}, ИНН{' '}
                      {order.address.companyInn}
                    </div>
                  )}
                  {order.address.type === 'individual' && (
                    <div>паспорт {order.address.passport}</div>
                  )}
                  {order.address.type === 'pickupPoint' && (
                    <div>ПВЗ {order.address.pickupPoint}</div>
                  )}
                  <div>{order.address.phone}</div>
                </>
              ) : (
                <div></div>
              )}
              {order.retailOrder?.passportData ? (
                <>
                  <div>
                    {order.retailOrder.passportData.surname}{' '}
                    {order.retailOrder.passportData.firstName}{' '}
                    {order.retailOrder.passportData.patronymic || ''}
                  </div>
                  <div>ИНН: {order.retailOrder.passportData.inn}</div>
                  <div>{order.retailOrder.passportData.email}</div>
                </>
              ) : (
                <div></div>
              )}
              {order.retailOrder?.pickupPoint ? (
                <>
                  <div>г.{order.retailOrder.pickupPoint.city}</div>
                  <div>
                    Адрес: {order.retailOrder.pickupPoint.address}(
                    {order.retailOrder.pickupPoint.lockerContractor})
                  </div>
                  <div>Телефон: {order.retailOrder.pickupPoint.userPhone}</div>
                </>
              ) : (
                <div></div>
              )}
            </InputWrapper>
            {order.cdekAddress ? (
              <InputWrapper
                theme="light"
                className="mt-3"
                title={intl.formatMessage({
                  defaultMessage: 'Адрес ПВЗ',
                  id: 'deliveryInfo.input-label.cdekAddress',
                  description: 'Заголовок поля ввода',
                })}
              >
                <div>{order.cdekAddress}</div>
              </InputWrapper>
            ) : null}
          </Rbac>
        </>
      )}
    </div>
  );
};
